import { faGear } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactElement } from 'react'
import { useRef, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { TextInput } from '~/components/Form/Input/TextInput'
import { InputGroup } from '~/components/Form/InputGroup'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { Dropdown } from '~/components/Global/Interactions/Dropdown/Dropdown'
import { useUpdateConfig } from '~/domains/configuration/mutations/updateConfig'
import { useConfig } from '~/domains/configuration/queries/config'
import type { ConfigName } from '~/domains/configuration/types'

export const CalendarConfigDropdown = (): ReactElement => {
  const [isConfigOpen, setIsConfigOpen] = useState(false)
  const configDropdownRef = useRef<HTMLDivElement>(null)
  const { data: startHour } = useConfig('dashboard.calendar_start_hour')
  const { data: endHour } = useConfig('dashboard.calendar_end_hour')
  const configMutation = useUpdateConfig()

  const handleButtonClick = () => {
    setIsConfigOpen(!isConfigOpen)
  }

  const handleConfigSave = useDebouncedCallback(
    (configKey: ConfigName, value: number) => {
      let startHourValue = startHour
      let endHourValue = endHour
      if (configKey === 'dashboard.calendar_start_hour') {
        startHourValue = value
      } else {
        endHourValue = value
      }

      if (
        Number.isNaN(endHourValue) ||
        Number.isNaN(startHourValue) ||
        endHourValue === undefined ||
        startHourValue === undefined ||
        endHourValue <= startHourValue
      ) {
        return
      }

      configMutation.mutate({
        name: configKey,
        value
      })
    },
    300
  )

  return (
    <div ref={configDropdownRef} style={{ position: 'relative' }}>
      <Button
        variation={ButtonVariation.Neutral}
        onClick={handleButtonClick}
        narrow
        buttonSize="extraSmall"
      >
        <FontAwesomeIcon icon={faGear} />
      </Button>
      <Dropdown
        isOpen={isConfigOpen}
        dropdownRef={configDropdownRef}
        onclose={() => setIsConfigOpen(false)}
        placement="bottom-end"
      >
        <InputGroup label="Heures de la journée">
          <TextInput
            type="number"
            standalone
            small
            min={0}
            max={23}
            defaultValue={startHour?.toString() ?? '8'}
            onChange={(e) => {
              const value = Number.parseInt(
                (e.target as HTMLInputElement).value,
                10
              )
              if (!Number.isNaN(value) && value >= 0 && value <= 23) {
                handleConfigSave('dashboard.calendar_start_hour', value)
              }
            }}
            trailingText="h"
          />
          <TextInput
            type="number"
            standalone
            small
            min={0}
            max={23}
            defaultValue={endHour?.toString() ?? '19'}
            onChange={(e) => {
              const value = Number.parseInt(
                (e.target as HTMLInputElement).value,
                10
              )
              if (!Number.isNaN(value) && value >= 0 && value <= 23) {
                handleConfigSave('dashboard.calendar_end_hour', value)
              }
            }}
            trailingText="h"
          />
        </InputGroup>
      </Dropdown>
    </div>
  )
}
